import { Children, memo, forwardRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { background } from 'theme/palette'
import { borderRadius, borderWidth, opacity } from 'theme/globals'
import styled from 'styled-components'
import { get } from 'lodash'
import Box from '@mui/material/Box'

const MIXINS = {
  paperButton: {
    outlined: true,
    round: 'extra',
    selectable: true,
    variantHover: 'neutral_300',
  },
  dashButton: {
    dash: true,
    variant: 'transparent',
    variantHover: 'neutral_400',
    round: 'extra',
    selectable: true,
  },
}

const StyledPaper = styled(Box)
  .withConfig({
    shouldForwardProp: prop => ![
      'active',
      'bgImg',
      'variant',
      'variantHover',
      'variantActive',
      'border',
      'borderHover',
      'borderStyle',
      'borderWidth',
      'depth',
      'depthHover',
      'depthActive',
      'round',
      'isLoading',
      'selectable',
      'outlined',
      'dash',
      'blend',
      'blendHover',
      'mixin',
      'noTransition',
    ].includes(prop),
  })
  .attrs(props => ({
    style: (props.bgImg && {
      backgroundImage: `url(${props.bgImg})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    }),
  }))`
  position: relative;
  isolation: isolate;
  
  ${props => `
    background-color: ${props.theme.palette.background[props.variant]?.main};
    color: ${props.theme.palette.background[props.variant]?.text};
    ${props.round ? `border-radius: ${props.theme.globals.borderRadius[typeof props.round === 'boolean' ? 'main' : props.round]}px;` : ''}
    ${props.outlined ? `border: ${props.theme.globals.border.main}; border-width: ${props.theme.globals.borderWidth.thick};` : ''}
    ${props.border ? `border: ${props.theme.globals.border[props.border]};` : ``}
    ${props.borderHover && !props.border && !props.outlined ? `border: ${props.theme.globals.border.transparent};` : ''}
    ${props.borderStyle ? `border-style: ${props.borderStyle};` : ``}
    ${props.borderWidth ? `border-width: ${props.theme.globals.borderWidth[props.borderWidth]};` : ``}
    box-shadow: ${props.depth ? props.theme.elevation[typeof props.depth === 'number' ? props.depth * 100 : props.depth] : 'none'};
    ${props.blend ? `opacity: ${get(props.theme, `globals.opacity.${props.blend}`, props.theme.globals.opacity.main)};` : ''}
    ${props.dash ? `background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='3' ry='3' stroke='%23DDE2E8' stroke-width='3' stroke-dasharray='6%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");` : ''}
    -ms-overflow-style: none;
    
    ${props.selectable ? 'cursor: pointer; user-select: none;' : ''}
    transition: ${props.noTransition ? 'none' : props.theme.internalMixins.setTransition(['box-shadow', 'background-color', 'color', 'border-color', 'border', 'opacity'], props.theme.state.timing.slow)};

    &:hover {
      ${props.depthHover ? `box-shadow: ${props.theme.elevation[typeof props.depthHover === 'number' ? props.depthHover * 100 : props.depthHover]};` : ''}
      ${props.variantHover ? `
        background-color: ${props.theme.palette.background[props.variantHover]?.main}; 
        color: ${props.theme.palette.background[props.variantHover]?.text};
      ` : ''}
      ${props.borderHover ? `border-color: ${props.theme.palette.border[props.borderHover]};` : ''}
      ${props.blendHover ? `opacity: ${get(props.theme, `globals.opacity.${props.blendHover}`, props.theme.globals.opacity.main)};` : ''}
    }

    &:active {
      ${props.depthActive ? `box-shadow: ${props.theme.elevation[typeof props.depthActive === 'number' ? props.depthActive * 100 : props.depthActive]};` : ''}
      ${props.variantActive ? `
        background-color: ${props.theme.palette.background[props.variantActive]?.main}; 
        color: ${props.theme.palette.background[props.variantActive]?.text};
      ` : ''}
      ${!props.variantActive && props.variantHover ? `
        background-color: ${props.theme.palette.background[props.variantHover]?.main}; 
        color: ${props.theme.palette.background[props.variantHover]?.text};
      ` : ''}
    }

    &::-webkit-scrollbar {
      display: none;
    }

  `}
  ${props => props.theme.internalMixins.blockLoad(props)}
`

const Paper = forwardRef((props, ref) => {
  const memoProps = useMemo(() => ({ ...props, ...(props.mixin && MIXINS[props.mixin]) }), [props])

  return (
    <StyledPaper
      {...memoProps}
      ref={ref}
      $variant={memoProps.bgImg ? 'inverse' : memoProps.variant}
    >
      {Children.toArray(memoProps.children)}
    </StyledPaper>
  )
})

Paper.propTypes = {
  bgImg: PropTypes.string,
  variant: PropTypes.oneOf(Object.keys(background)),
  variantHover: PropTypes.oneOf(Object.keys(background)),
  variantActive: PropTypes.oneOf(Object.keys(background)),
  border: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  borderHover: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  borderStyle: PropTypes.string,
  borderWidth: PropTypes.oneOf(Object.keys(borderWidth)),
  depth: PropTypes.any,
  depthHover: PropTypes.any,
  depthActive: PropTypes.any,
  round: PropTypes.oneOf([true, false, ...Object.keys(borderRadius)]),
  isLoading: PropTypes.bool,
  selectable: PropTypes.bool,
  outlined: PropTypes.bool,
  dash: PropTypes.bool,
  noTransition: PropTypes.bool,
  blend: PropTypes.oneOf([true, false, ...Object.keys(opacity)]),
  blendHover: PropTypes.oneOf([true, false, ...Object.keys(opacity)]),
  mixin: PropTypes.oneOf(Object.keys(MIXINS)),
}

Paper.defaultProps = {
  variant: 'paper',
  isLoading: false,
}

export default memo(Paper)
